import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Play,
  Eye,
  MessageCircle,
  Volume2,
  VolumeX,
  Grid,
  List,
  Clock,
  Maximize,
  Minimize,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { MediaViewer } from "@/components/MediaViewer";
import { mediaCache, ClientCache } from "@/lib/cache";
import { useFullscreen } from "@/lib/use-fullscreen";
import { useWakeLock } from "@/lib/use-wake-lock";

const FULLSCREEN_PREFERENCE_KEY = "rgpv:feed-fullscreen";

interface Media {
  id: number;
  user_id: number;
  uuid: string;
  url: string;
  mime_type: string;
  file_size?: number;
  original_filename?: string;
  title?: string;
  description?: string;
  tags?: string[];
  r_user?: string;
  r_subreddit?: string;
  source?: string;
  people?: string[];
  time_watched: number;
  view_count: number;
  thumbnail_url?: string;
  thumbnail_generated: boolean;
  created_at: string;
  updated_at: string;
}

export function HomePage() {
  const { user, token } = useAuth();
  const [media, setMedia] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const [sortBy, setSortBy] = useState<
    "created_at" | "view_count" | "time_watched" | "random"
  >("random");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [allTags, setAllTags] = useState<string[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [showViewer, setShowViewer] = useState(false);

  // Mobile fullscreen state
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "fullscreen">("grid");
  const [userPreferredGridMode, setUserPreferredGridMode] = useState(false);
  const [prefersFullscreen, setPrefersFullscreen] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(FULLSCREEN_PREFERENCE_KEY) === "true"
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<"up" | "down" | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Video controls state for mobile fullscreen
  const [showVideoControls, setShowVideoControls] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const videoControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // GIF controls state
  const [gifPaused, setGifPaused] = useState(false);
  const [gifOriginalSrc, setGifOriginalSrc] = useState<string>("");
  const [gifStaticSrc, setGifStaticSrc] = useState<string>("");

  // Time tracking state
  const [currentViewStartTime, setCurrentViewStartTime] = useState<
    number | null
  >(null);
  const [currentViewMediaId, setCurrentViewMediaId] = useState<number | null>(
    null
  );
  const trackingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Lazy loading state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingForNavigation, setIsLoadingForNavigation] = useState(false);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Random seed for consistent random sorting during the session
  const [randomSeed] = useState(() => Math.floor(Math.random() * 1000000));

  // Fullscreen functionality
  const {
    isFullscreen: isFullscreenEnabled,
    isSupported: isFullscreenSupported,
    enter,
    exit: exitFullscreen,
  } = useFullscreen();

  const enterFullscreen = useCallback(() => {
    setPrefersFullscreen(true);
    window.localStorage.setItem(FULLSCREEN_PREFERENCE_KEY, "true");
    return enter(containerRef.current);
  }, [enter]);

  const leaveFullscreen = useCallback(() => {
    setPrefersFullscreen(false);
    window.localStorage.setItem(FULLSCREEN_PREFERENCE_KEY, "false");
    return exitFullscreen();
  }, [exitFullscreen]);

  // Keep the screen awake for as long as the feed is open
  useWakeLock(viewMode === "fullscreen");

  // Requesting fullscreen only works from a user gesture, so when the feed
  // opens on its own we wait for the next tap or swipe to ask for it.
  useEffect(() => {
    if (viewMode !== "fullscreen") return;
    if (!prefersFullscreen || isFullscreenEnabled || !isFullscreenSupported)
      return;

    const handleGesture = (event: PointerEvent) => {
      // Buttons may close the feed, so let them be handled first
      const target = event.target as HTMLElement | null;
      if (target?.closest("button")) return;

      document.removeEventListener("pointerdown", handleGesture);
      enter(containerRef.current);
    };

    document.addEventListener("pointerdown", handleGesture);
    return () => document.removeEventListener("pointerdown", handleGesture);
  }, [
    viewMode,
    prefersFullscreen,
    isFullscreenEnabled,
    isFullscreenSupported,
    enter,
  ]);

  // Never stay in browser fullscreen once the feed is closed
  useEffect(() => {
    if (viewMode !== "fullscreen" && isFullscreenEnabled) {
      exitFullscreen();
    }
  }, [viewMode, isFullscreenEnabled, exitFullscreen]);

  // Stop the page behind the feed from scrolling or bouncing
  useEffect(() => {
    if (viewMode !== "fullscreen") return;

    document.body.classList.add("feed-immersive");
    return () => document.body.classList.remove("feed-immersive");
  }, [viewMode]);

  // Use media directly since filtering is now done server-side
  const filteredMedia = media;

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Reset pagination when search changes
      setPage(1);
      setHasMore(true);
      setSelectedMedia(null); // Reset selected media when search changes
      setCurrentIndex(0); // Reset current index
      fetchMedia(1, false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    // Reset pagination when filters change (immediate, no debounce needed)
    setPage(1);
    setHasMore(true);
    setSelectedMedia(null); // Reset selected media when filters change
    setCurrentIndex(0); // Reset current index
    fetchMedia(1, false);
  }, [sortBy, sortOrder, selectedTag]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-switch to fullscreen on mobile (unless user prefers grid)
  useEffect(() => {
    if (isMobile && filteredMedia.length > 0 && !userPreferredGridMode) {
      setViewMode("fullscreen");
      // Set the first media item as selected and start tracking
      if (filteredMedia[currentIndex] && !selectedMedia) {
        setSelectedMedia(filteredMedia[currentIndex]);
        startTimeTracking(filteredMedia[currentIndex].id);
      }
    } else if (!isMobile) {
      stopTimeTracking();
      setViewMode("grid");
      // Exit fullscreen when going back to grid
      if (isFullscreenEnabled) {
        exitFullscreen();
      }
      setSelectedMedia(null); // Close any open media viewer
      setShowViewer(false); // Ensure viewer is closed
      setUserPreferredGridMode(false); // Reset preference when not mobile
    }
  }, [
    isMobile,
    filteredMedia.length,
    selectedMedia,
    userPreferredGridMode,
    isFullscreenEnabled,
  ]);

  // Update selected media when filtered media changes (e.g., after sorting/filtering)
  useEffect(() => {
    if (isMobile && viewMode === "fullscreen" && filteredMedia.length > 0) {
      // If we have a selected media, check if it's still in the filtered list
      if (selectedMedia) {
        const stillExists = filteredMedia.some(
          (item) => item.id === selectedMedia.id
        );
        if (!stillExists) {
          // Selected media is no longer in the filtered list, reset to first item
          setSelectedMedia(filteredMedia[0]);
          setCurrentIndex(0);
          startTimeTracking(filteredMedia[0].id);
        }
      } else {
        // No selected media, set to first item
        setSelectedMedia(filteredMedia[0]);
        setCurrentIndex(0);
        startTimeTracking(filteredMedia[0].id);
      }
    }
  }, [filteredMedia, isMobile, viewMode, selectedMedia]);

  // Touch navigation
  const handleScroll = useCallback(
    (e: WheelEvent | TouchEvent) => {
      if (
        viewMode !== "fullscreen" ||
        filteredMedia.length === 0 ||
        isTransitioning
      )
        return;

      e.preventDefault();

      let deltaY = 0;
      if ("deltaY" in e) {
        deltaY = e.deltaY;
      } else if ("touches" in e && e.touches.length > 0) {
        // Handle touch events
        return;
      }

      if (Math.abs(deltaY) < 50) return; // Threshold for scroll

      if (deltaY > 0) {
        // Scroll down - next media
        setIsTransitioning(true);
        setSwipeDirection("up");

        stopTimeTracking();

        setTimeout(() => {
          getNextMedia();
          setSwipeDirection(null);

          setTimeout(() => {
            setIsTransitioning(false);
          }, 50);
        }, 150);
      } else if (deltaY < 0) {
        // Scroll up - previous media
        setIsTransitioning(true);
        setSwipeDirection("down");

        stopTimeTracking();

        setTimeout(() => {
          getPreviousMedia();
          setSwipeDirection(null);

          setTimeout(() => {
            setIsTransitioning(false);
          }, 50);
        }, 150);
      }
    },
    [viewMode, filteredMedia.length, currentIndex, isTransitioning]
  );

  // Touch handling for mobile
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't interfere with button clicks
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest('[role="button"]')) {
      console.log("Touch start on button, ignoring");
      return;
    }

    console.log("Touch start registered");
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (viewMode !== "fullscreen" || isTransitioning) return;

    // Don't interfere with button clicks
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest('[role="button"]')) {
      console.log("Touch end on button, ignoring");
      return;
    }

    const touchEndY = e.changedTouches[0].clientY;
    const touchEndX = e.changedTouches[0].clientX;
    const deltaY = touchStartY.current - touchEndY;
    const deltaX = touchStartX.current - touchEndX;

    const minSwipeDistance = 50; // Minimum distance for a swipe

    // Check if the swipe distance is significant enough
    if (
      Math.abs(deltaX) < minSwipeDistance &&
      Math.abs(deltaY) < minSwipeDistance
    ) {
      return; // Not a significant swipe
    }

    // Determine if the swipe is more horizontal or vertical
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);

    // For horizontal swipes, require a stronger threshold to avoid accidental triggers
    const isStrongHorizontalSwipe =
      Math.abs(deltaX) > minSwipeDistance &&
      Math.abs(deltaX) > Math.abs(deltaY) * 1.5;

    if (isStrongHorizontalSwipe) {
      // Horizontal swipe - close the feed view
      e.preventDefault();
      e.stopPropagation();
      console.log("Horizontal swipe detected, closing feed view");
      setUserPreferredGridMode(true); // User explicitly chose grid mode
      stopTimeTracking();
      setViewMode("grid");
      // Exit fullscreen when going to grid
      if (isFullscreenEnabled) {
        exitFullscreen();
      }
      setSelectedMedia(null); // Close any open media viewer
      setShowViewer(false); // Ensure viewer is closed
      return;
    }

    // Vertical swipe handling (only if it's clearly vertical)
    if (deltaY > 0) {
      // Swipe up - next media
      e.preventDefault();
      e.stopPropagation();
      setIsTransitioning(true);
      setSwipeDirection("up");

      // Stop tracking current media
      stopTimeTracking();

      setTimeout(() => {
        getNextMedia();
        setSwipeDirection(null);

        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    } else if (deltaY < 0) {
      // Swipe down - previous media
      e.preventDefault();
      e.stopPropagation();
      setIsTransitioning(true);
      setSwipeDirection("down");

      // Stop tracking current media
      stopTimeTracking();

      setTimeout(() => {
        getPreviousMedia();
        setSwipeDirection(null);

        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    }
  };

  // Keyboard navigation and wheel events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode !== "fullscreen" || isTransitioning) return;

      if (e.key === "ArrowDown") {
        setIsTransitioning(true);
        setSwipeDirection("up");

        // Stop tracking current media
        stopTimeTracking();

        setTimeout(() => {
          getNextMedia();
          setSwipeDirection(null);

          setTimeout(() => {
            setIsTransitioning(false);
          }, 50);
        }, 150);
      } else if (e.key === "ArrowUp") {
        setIsTransitioning(true);
        setSwipeDirection("down");

        // Stop tracking current media
        stopTimeTracking();

        setTimeout(() => {
          getPreviousMedia();
          setSwipeDirection(null);

          setTimeout(() => {
            setIsTransitioning(false);
          }, 50);
        }, 150);
      } else if (e.key === "Escape") {
        setUserPreferredGridMode(true); // User explicitly chose grid mode
        stopTimeTracking();
        setViewMode("grid");
        // Exit fullscreen when going to grid
        if (isFullscreenEnabled) {
          exitFullscreen();
        }
        setSelectedMedia(null); // Close any open media viewer
        setShowViewer(false); // Ensure viewer is closed
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleScroll);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleScroll);
    };
  }, [viewMode, currentIndex, filteredMedia.length, handleScroll]);

  // Cleanup time tracking when component unmounts or view mode changes
  useEffect(() => {
    return () => {
      stopTimeTracking();
      if (videoControlsTimeoutRef.current) {
        clearTimeout(videoControlsTimeoutRef.current);
      }
    };
  }, []);

  // Stop tracking when switching to grid mode
  useEffect(() => {
    if (viewMode === "grid") {
      stopTimeTracking();
      setShowVideoControls(false);
      if (videoControlsTimeoutRef.current) {
        clearTimeout(videoControlsTimeoutRef.current);
      }
    }
  }, [viewMode]);

  // Reset video controls when switching between videos
  useEffect(() => {
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setShowVideoControls(false);
    setGifPaused(false); // Reset GIF state
    if (videoControlsTimeoutRef.current) {
      clearTimeout(videoControlsTimeoutRef.current);
    }
  }, [selectedMedia?.id]);

  // Handle navigation when new media is loaded for navigation
  useEffect(() => {
    if (!isLoadingForNavigation && selectedMedia && hasMore) {
      const currentIndex = filteredMedia.findIndex(
        (item) => item.id === selectedMedia.id
      );

      // If we were at the end and new media was loaded, navigate to the next item
      if (currentIndex === filteredMedia.length - 2) {
        const nextIndex = currentIndex + 1;
        if (nextIndex < filteredMedia.length) {
          setSelectedMedia(filteredMedia[nextIndex]);
          startTimeTracking(filteredMedia[nextIndex].id);
        }
      }
    }
  }, [filteredMedia.length, isLoadingForNavigation, selectedMedia, hasMore]);

  const fetchMedia = useCallback(
    async (
      pageNum: number = 1,
      append: boolean = false,
      forNavigation: boolean = false
    ) => {
      try {
        if (pageNum === 1) {
          setLoading(true);
        } else {
          if (forNavigation) {
            setIsLoadingForNavigation(true);
          } else {
            setIsLoadingMore(true);
          }
        }

        const params = {
          page: pageNum.toString(),
          limit: "12", // Load 12 items per page for better performance
          sortBy: sortBy,
          sortOrder: sortOrder,
          ...(selectedTag && selectedTag !== "all" && { tags: selectedTag }),
          ...(searchQuery && { search: searchQuery }),
          ...(sortBy === "random" && { randomSeed: randomSeed.toString() }),
        };

        // Generate cache key
        const cacheKey = ClientCache.generateKey("/api/media", params);

        // Check cache first (only for first page)
        if (pageNum === 1 && !append) {
          const cachedData = mediaCache.get<{
            media: Media[];
            pagination: any;
          }>(cacheKey);
          if (cachedData) {
            setMedia(cachedData.media || []);
            setHasMore(
              cachedData.pagination &&
                cachedData.pagination.page < cachedData.pagination.pages
            );
            setPage(pageNum);

            // Extract unique tags from cached data
            const tags = new Set<string>();
            cachedData.media?.forEach((item: Media) => {
              item.tags?.forEach((tag: string) => tags.add(tag));
            });
            setAllTags(Array.from(tags).sort());

            setLoading(false);
            return;
          }
        }

        const queryString = new URLSearchParams(params).toString();
        const response = await fetch(`/api/media?${queryString}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch media");
        }

        const data = await response.json();

        if (append) {
          setMedia((prev) => [...prev, ...(data.media || [])]);
        } else {
          setMedia(data.media || []);
          // Cache the response for first page
          mediaCache.set(cacheKey, data);
        }

        // Update pagination state
        setHasMore(
          data.pagination && data.pagination.page < data.pagination.pages
        );
        setPage(pageNum);

        // Extract unique tags
        const tags = new Set<string>();
        data.media?.forEach((item: Media) => {
          item.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAllTags(Array.from(tags).sort());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
        setIsLoadingForNavigation(false);
      }
    },
    [sortBy, sortOrder, selectedTag, searchQuery, token]
  );

  // Intersection observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoadingMore && !loading) {
          fetchMedia(page + 1, true);
        }
      },
      {
        rootMargin: "100px", // Start loading when 100px away from bottom
        threshold: 0.1,
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, isLoadingMore, loading, page, fetchMedia]);

  const openMediaViewer = (mediaItem: Media) => {
    setSelectedMedia(mediaItem);
    setShowViewer(true);
    startTimeTracking(mediaItem.id);
  };

  const closeMediaViewer = () => {
    setShowViewer(false);
    setSelectedMedia(null);
    stopTimeTracking();
  };

  const getNextMedia = () => {
    if (!selectedMedia) return;
    const currentIndex = filteredMedia.findIndex(
      (item) => item.id === selectedMedia.id
    );

    // If we're at the end of loaded media and there's more to load
    if (
      currentIndex === filteredMedia.length - 1 &&
      hasMore &&
      !isLoadingMore &&
      !isLoadingForNavigation
    ) {
      // Load more media for navigation
      fetchMedia(page + 1, true, true);
    } else {
      // Normal navigation within loaded media
      const nextIndex = (currentIndex + 1) % filteredMedia.length;
      setSelectedMedia(filteredMedia[nextIndex]);
      startTimeTracking(filteredMedia[nextIndex].id);
    }
  };

  const getPreviousMedia = () => {
    if (!selectedMedia) return;
    const currentIndex = filteredMedia.findIndex(
      (item) => item.id === selectedMedia.id
    );

    // Normal navigation (can't go back to unloaded items)
    const prevIndex =
      currentIndex === 0 ? filteredMedia.length - 1 : currentIndex - 1;
    setSelectedMedia(filteredMedia[prevIndex]);
    startTimeTracking(filteredMedia[prevIndex].id);
  };

  const getMediaUrl = (uuid: string) => {
    // Use public UUID-based endpoint (no auth required)
    return `/api/public/media/${uuid}`;
  };

  const getThumbnailUrl = (uuid: string) => {
    // Use public thumbnail endpoint (no auth required)
    return `/api/public/thumbnails/${uuid}`;
  };

  // Time tracking functions
  const startTimeTracking = (mediaId: number) => {
    // Stop any existing tracking
    stopTimeTracking();

    // Start new tracking
    setCurrentViewStartTime(Date.now());
    setCurrentViewMediaId(mediaId);

    // Set a timeout to record view after 5 seconds of continuous viewing
    trackingTimeoutRef.current = setTimeout(() => {
      recordViewTime(mediaId, 5);
    }, 5000);
  };

  const stopTimeTracking = () => {
    if (currentViewStartTime && currentViewMediaId) {
      const timeWatched = Math.floor(
        (Date.now() - currentViewStartTime) / 1000
      );

      // Only record if there's no timeout running (prevents duplicate calls)
      if (timeWatched > 0 && !trackingTimeoutRef.current) {
        recordViewTime(currentViewMediaId, timeWatched);
      }
    }

    setCurrentViewStartTime(null);
    setCurrentViewMediaId(null);

    if (trackingTimeoutRef.current) {
      clearTimeout(trackingTimeoutRef.current);
      trackingTimeoutRef.current = null;
    }
  };

  const recordViewTime = async (mediaId: number, timeWatched: number) => {
    try {
      const response = await fetch(`/api/media/${mediaId}/view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ timeWatched }),
      });

      if (!response.ok) {
        console.error("Failed to record view time:", response.statusText);
      }
    } catch (error) {
      console.error("Error recording view time:", error);
    }
  };

  // Video controls functions for mobile fullscreen
  const showVideoControlsTemporarily = () => {
    setShowVideoControls(true);

    // Clear existing timeout
    if (videoControlsTimeoutRef.current) {
      clearTimeout(videoControlsTimeoutRef.current);
    }

    // Hide controls after 3 seconds
    videoControlsTimeoutRef.current = setTimeout(() => {
      setShowVideoControls(false);
    }, 3000);
  };

  const handleVideoTimeUpdate = () => {
    if (!videoRef.current) return;
    setVideoCurrentTime(videoRef.current.currentTime);
  };

  const handleVideoLoadedMetadata = () => {
    if (!videoRef.current) return;
    setVideoDuration(videoRef.current.duration);
    // Show controls briefly when video loads on mobile
    if (isMobile) {
      showVideoControlsTemporarily();
    }
  };

  const handleVideoSeek = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    let clientX: number;

    if ("touches" in e) {
      // Touch event
      clientX = e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0;
    } else {
      // Mouse event
      clientX = e.clientX;
    }

    const percent = (clientX - rect.left) / rect.width;
    const newTime = Math.max(
      0,
      Math.min(videoDuration, percent * videoDuration)
    );

    videoRef.current.currentTime = newTime;
    setVideoCurrentTime(newTime);

    // Keep controls visible when seeking
    showVideoControlsTemporarily();
  };

  const handleVideoClick = () => {
    const currentMedia = filteredMedia[currentIndex];
    if (!currentMedia || !videoRef.current) return;

    // For GIFs, toggle play/pause directly
    if (currentMedia.mime_type === "image/gif") {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      showVideoControlsTemporarily();
      return;
    }

    // For videos on mobile/touch devices, toggle controls visibility
    if ("ontouchstart" in window) {
      if (showVideoControls) {
        setShowVideoControls(false);
        if (videoControlsTimeoutRef.current) {
          clearTimeout(videoControlsTimeoutRef.current);
        }
      } else {
        showVideoControlsTemporarily();
      }
    }
  };

  const formatVideoTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatTimeWatched = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // GIF control functions
  const handleGifClick = () => {
    const currentMedia = filteredMedia[currentIndex];
    if (!currentMedia || currentMedia.mime_type !== "image/gif") return;

    setGifPaused(!gifPaused);

    // Toggle controls visibility for GIFs too
    if (showVideoControls) {
      setShowVideoControls(false);
      if (videoControlsTimeoutRef.current) {
        clearTimeout(videoControlsTimeoutRef.current);
      }
    } else {
      showVideoControlsTemporarily();
    }
  };

  const generateGifStaticFrame = (gifUrl: string) => {
    // For now, we'll use the same URL but could implement server-side static frame generation
    // This is a placeholder - ideally we'd have a static frame endpoint
    return gifUrl;
  };

  const renderMobileFullscreen = () => {
    if (filteredMedia.length === 0 || !selectedMedia) return null;

    const currentMedia = selectedMedia;

    // Animation classes based on swipe direction
    const getAnimationClass = () => {
      if (!isTransitioning || !swipeDirection) return "";

      return swipeDirection === "up"
        ? "animate-slide-up"
        : "animate-slide-down";
    };

    return (
      <div
        ref={containerRef}
        className="fixed inset-0 w-screen h-[100dvh] bg-black z-50 overflow-hidden touch-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Media Content */}
        <div
          className={`relative w-full h-full flex items-center justify-center transition-transform duration-150 ease-out ${getAnimationClass()}`}
        >
          {currentMedia.mime_type.startsWith("image/") &&
          currentMedia.mime_type !== "image/gif" ? (
            <img
              src={getMediaUrl(currentMedia.uuid)}
              alt={
                currentMedia.title || currentMedia.original_filename || "Media"
              }
              className="w-full h-full object-contain"
            />
          ) : currentMedia.mime_type === "image/gif" ? (
            <div className="relative group w-full h-full flex items-center justify-center">
              <img
                src={
                  gifPaused
                    ? generateGifStaticFrame(getMediaUrl(currentMedia.uuid))
                    : getMediaUrl(currentMedia.uuid)
                }
                alt={
                  currentMedia.title || currentMedia.original_filename || "GIF"
                }
                className="w-full h-full object-contain cursor-pointer"
                onClick={handleGifClick}
              />

              {/* GIF Controls Overlay */}
              <div
                className={`absolute bottom-48 left-4 right-4 bg-gradient-to-t from-black/70 to-transparent p-3 rounded-lg transition-opacity ${
                  showVideoControls
                    ? "opacity-100"
                    : "opacity-0 md:group-hover:opacity-100"
                }`}
              >
                {/* Simulated Progress Bar for GIFs */}
                <div className="w-full h-3 md:h-2 bg-white/30 rounded mb-2">
                  <div
                    className={`h-full bg-white rounded ${
                      gifPaused ? "" : "animate-pulse"
                    }`}
                    style={{ width: "100%" }}
                  />
                </div>

                {/* GIF Info */}
                <div className="flex items-center justify-between text-white text-sm">
                  <span className="flex items-center gap-2">
                    <span className="text-xs bg-white/20 px-2 py-1 rounded">
                      GIF
                    </span>
                    {gifPaused ? "Paused" : "Playing"}
                  </span>
                  <div className="text-xs opacity-75">Tap to hide controls</div>
                </div>
              </div>
            </div>
          ) : currentMedia.mime_type.startsWith("video/") ? (
            <div className="relative group w-full h-full flex items-center justify-center">
              <video
                ref={videoRef}
                src={getMediaUrl(currentMedia.uuid)}
                poster={
                  currentMedia.thumbnail_generated
                    ? getThumbnailUrl(currentMedia.uuid)
                    : undefined
                }
                className="w-full h-full object-contain"
                autoPlay
                muted={isVideoMuted}
                loop
                playsInline
                controls={false}
                onClick={handleVideoClick}
                onTimeUpdate={handleVideoTimeUpdate}
                onLoadedMetadata={handleVideoLoadedMetadata}
              />

              {/* Video Controls Overlay - Mobile friendly */}
              {currentMedia.mime_type.startsWith("video/") && (
                <div
                  className={`absolute bottom-48 left-4 right-4 bg-gradient-to-t from-black/70 to-transparent p-3 rounded-lg transition-opacity ${
                    showVideoControls
                      ? "opacity-100"
                      : "opacity-0 md:group-hover:opacity-100"
                  }`}
                >
                  {/* Progress Bar - Functional for both videos and GIFs */}
                  <div
                    className="w-full h-3 md:h-2 bg-white/30 rounded cursor-pointer mb-2 touch-manipulation"
                    onClick={handleVideoSeek}
                    onTouchEnd={handleVideoSeek}
                  >
                    <div
                      className="h-full bg-white rounded"
                      style={{
                        width: `${(videoCurrentTime / videoDuration) * 100}%`,
                      }}
                    />
                  </div>

                  {/* Time Display */}
                  <div className="flex items-center justify-between text-white text-sm">
                    <span>
                      {formatVideoTime(videoCurrentTime)} /{" "}
                      {formatVideoTime(videoDuration)}
                    </span>
                    <div className="text-xs opacity-75">
                      Tap to hide controls
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white">
              <span className="text-lg">{currentMedia.mime_type}</span>
            </div>
          )}

          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent p-4 pt-[max(1rem,env(safe-area-inset-top))] z-10">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onPointerDown={(e) => {
                  console.log("Grid button clicked");
                  e.preventDefault();
                  e.stopPropagation();
                  setUserPreferredGridMode(true); // User explicitly chose grid mode
                  setViewMode("grid");
                  // Exit fullscreen when going to grid
                  if (isFullscreenEnabled) {
                    exitFullscreen();
                  }
                  setSelectedMedia(null); // Close any open media viewer
                  setShowViewer(false); // Ensure viewer is closed
                }}
                className="text-white hover:bg-white/20 relative z-50"
                style={{ touchAction: "manipulation" }}
              >
                <Grid className="h-4 w-4 mr-2" />
                Grid
              </Button>

              {isFullscreenSupported && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    isFullscreenEnabled ? "Exit fullscreen" : "Enter fullscreen"
                  }
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isFullscreenEnabled) {
                      leaveFullscreen();
                    } else {
                      enterFullscreen();
                    }
                  }}
                  className="text-white hover:bg-white/20 relative z-50"
                  style={{ touchAction: "manipulation" }}
                >
                  {isFullscreenEnabled ? (
                    <Minimize className="h-5 w-5" />
                  ) : (
                    <Maximize className="h-5 w-5" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="absolute right-4 bottom-56 flex flex-col gap-4 z-10">
            {/* Mute/Unmute for videos */}
            {currentMedia.mime_type.startsWith("video/") && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsVideoMuted(!isVideoMuted)}
                className="bg-black/50 text-white hover:bg-black/70 rounded-full w-12 h-12"
              >
                {isVideoMuted ? (
                  <VolumeX className="h-6 w-6" />
                ) : (
                  <Volume2 className="h-6 w-6" />
                )}
              </Button>
            )}
          </div>

          {/* Bottom Overlay with Meta-data - Only shown when controls are visible */}
          {showVideoControls && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-10">
              <div className="text-white space-y-2">
                {/* Title */}
                <h3
                  className="text-lg font-semibold line-clamp-2"
                  style={{ maxWidth: "72vw", flexShrink: 1 }}
                >
                  {currentMedia.title ||
                    currentMedia.original_filename ||
                    "Untitled"}
                </h3>

                {/* Compact Reddit info */}
                {(currentMedia.r_user || currentMedia.r_subreddit) && (
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    {currentMedia.r_user && (
                      <span className="bg-white/20 px-2 py-1 rounded">
                        u/{currentMedia.r_user}
                      </span>
                    )}
                    {currentMedia.r_subreddit && (
                      <span className="bg-white/20 px-2 py-1 rounded">
                        r/{currentMedia.r_subreddit}
                      </span>
                    )}
                  </div>
                )}

                {/* Tags */}
                {currentMedia.tags && currentMedia.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {currentMedia.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-white/20 text-white text-xs rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                    {currentMedia.tags.length > 4 && (
                      <span className="px-2 py-1 bg-white/20 text-white text-xs rounded-full">
                        +{currentMedia.tags.length - 4}
                      </span>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-gray-300">
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    <span>{currentMedia.view_count} views</span>
                  </div>
                  <span>
                    {new Date(currentMedia.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation indicators */}
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/50 text-xs">
            ↑
          </div>
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 translate-y-8 text-white/50 text-xs">
            ↓
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading media...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4">Error: {error}</p>
            <Button onClick={() => fetchMedia(1, false)}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render mobile fullscreen view if in fullscreen mode
  if (viewMode === "fullscreen") {
    return renderMobileFullscreen();
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Media Feed</h1>
          <div className="flex items-center gap-2">
            <p className="text-muted-foreground hidden sm:block">
              Welcome back, {user?.username}!
            </p>
            {/* Mobile fullscreen toggle */}
            {isMobile && filteredMedia.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setUserPreferredGridMode(false); // User chose fullscreen mode
                  setViewMode("fullscreen");
                  // Enter fullscreen when going to feed view on mobile
                  if (isMobile && !isFullscreenEnabled) {
                    enterFullscreen();
                  }
                }}
              >
                <List className="h-4 w-4 mr-2" />
                Feed
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search media by title, description, tags, or source..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Select value={selectedTag} onValueChange={setSelectedTag}>
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {allTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={sortBy}
              onValueChange={(value: any) => setSortBy(value)}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="random">Random</SelectItem>
                <SelectItem value="created_at">Date</SelectItem>
                <SelectItem value="view_count">Views</SelectItem>
                <SelectItem value="time_watched">Watch time</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              {sortOrder === "asc" ? (
                <SortAsc className="h-4 w-4" />
              ) : (
                <SortDesc className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      {filteredMedia.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">No media found</p>
          <Button asChild>
            <a href="/manage">Upload your first media file</a>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredMedia.map((item) => (
            <Card
              key={item.id}
              className="group cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
              onClick={() => openMediaViewer(item)}
            >
              <div className="relative aspect-square bg-muted">
                {item.mime_type.startsWith("image/") ? (
                  <img
                    src={
                      item.thumbnail_generated
                        ? getThumbnailUrl(item.uuid)
                        : getMediaUrl(item.uuid)
                    }
                    alt={item.title || item.original_filename || "Media"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback to original media if thumbnail fails to load
                      const target = e.target as HTMLImageElement;
                      if (target.src !== getMediaUrl(item.uuid)) {
                        target.src = getMediaUrl(item.uuid);
                      }
                    }}
                  />
                ) : item.mime_type.startsWith("video/") ? (
                  <div className="relative w-full h-full">
                    <video
                      src={getMediaUrl(item.uuid)}
                      poster={
                        item.thumbnail_generated
                          ? getThumbnailUrl(item.uuid)
                          : undefined
                      }
                      className="w-full h-full object-cover"
                      preload="none"
                      muted
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                      <Play className="h-12 w-12 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-muted-foreground text-sm">
                      {item.mime_type}
                    </span>
                  </div>
                )}
              </div>

              <CardContent className="p-4">
                <h3 className="font-semibold truncate mb-1">
                  {item.title || item.original_filename || "Untitled"}
                </h3>
                {item.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {item.description}
                  </p>
                )}

                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {item.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {item.tags.length > 3 && (
                      <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded">
                        +{item.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    <span>{item.view_count}</span>
                  </div>
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                </div>

                {/* Time watched */}
                {item.time_watched > 0 && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                    <Clock className="h-3 w-3" />
                    <span>{formatTimeWatched(item.time_watched)}</span>
                  </div>
                )}

                {/* Source links */}
                {(item.r_user || item.r_subreddit || item.source) && (
                  <div className="flex flex-wrap gap-1 text-xs">
                    {item.r_user && (
                      <a
                        href={`https://reddit.com/user/${item.r_user}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        u/{item.r_user}
                      </a>
                    )}
                    {item.r_subreddit && (
                      <a
                        href={`https://reddit.com/r/${item.r_subreddit}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        r/{item.r_subreddit}
                      </a>
                    )}
                    {!item.r_user && !item.r_subreddit && item.source && (
                      <a
                        href={item.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 underline truncate"
                        onClick={(e) => e.stopPropagation()}
                        title={item.source}
                      >
                        Source
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Loading indicator for lazy loading */}
      {hasMore && (
        <div ref={loadingRef} className="flex justify-center py-8">
          {isLoadingMore ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span>Loading more media...</span>
            </div>
          ) : (
            <div className="h-4"></div> // Invisible element for intersection observer
          )}
        </div>
      )}

      {/* Media Viewer */}
      {selectedMedia && (
        <MediaViewer
          media={selectedMedia}
          isOpen={showViewer}
          onClose={closeMediaViewer}
          onNext={
            filteredMedia.length > 1 || (hasMore && !isLoadingForNavigation)
              ? getNextMedia
              : undefined
          }
          onPrevious={filteredMedia.length > 1 ? getPreviousMedia : undefined}
          isLoadingNext={isLoadingForNavigation}
        />
      )}
    </div>
  );
}
